import { useState, useMemo } from 'react'
import { message as $message } from 'antd'
import cloneDeep from 'lodash/cloneDeep'
import arrayMove from 'array-move'
import { createAPI } from '@/utils/modal'
import AddProcessingResultsModal from '@/modules/afterSale/components/AddProcessingResultsModal'
import { saveProcessResult, getProcessResult } from '@/services/afterSale'

const MAX_LIST_LENGTH = 500
export const clearLabel = '{清空}'

export interface ItemType {
    text: string
    system?: boolean
    show?: boolean
}

export interface Option {
    label: string
    value: string
    key: string
    title: string
}

export interface AfterSalesProcessingResults {
    // 【处理结果】列表
    list: ItemType[]
    // 【处理结果】下拉
    options: ItemType[]
    // 编辑弹窗【处理结果】列表
    tempList: ItemType[]
    // 编辑弹窗【处理结果】列表勾选状态
    tempCheckedValue: Record<string, boolean>
    // 获取【处理结果】列表Loading
    loading: boolean
    // 更新【处理结果】列表Loading
    updateLoading: boolean
    // 获取【处理结果】列表
    getProcessingResults: () => void
    // 更新【处理结果】列表
    updateList: () => Promise<void>
    // 编辑【处理结果】
    editProcessingResults: () => void
    // 新增【处理结果】
    addProcessingResult: (label: string) => boolean
    // 排序【处理结果】
    sortProcessingResult: (oldIndex: number, newIndex: number) => void
    // 勾选/取消勾选【处理结果】
    checkProcessingResult: (text: string, checked: boolean) => void
    // 删除【处理结果】
    deleteProcessingResult: (text: string) => void
}

const useAfterSalesProcessingResults: () => AfterSalesProcessingResults = () => {
    const [list, setList] = useState<ItemType[]>([])
    const [tempList, setTempList] = useState<ItemType[]>([])
    const [tempCheckedValue, setTempCheckedValue] = useState<Record<string, boolean>>({})
    const [loading, setLoading] = useState<boolean>(false)
    const [updateLoading, setUpdateLoading] = useState(false)

    const getProcessingResults = async () => {
        setLoading(true)
        try {
            const { success, data, message } = await getProcessResult()
            if (success) {
                setList(data || [])
                setLoading(false)
                return Promise.resolve(true)
            } else {
                $message.error(message)
            }
        } catch (error) {
            console.error(error)
        }
        setLoading(false)
    }

    const updateList = () => {
        const payload = tempList.map((_, index) => ({
            text: _.text,
            order: index,
            system: _.system,
            show: _.system ? tempCheckedValue[_.text] : true
        }))

        return new Promise<void>((resolve, reject) => {
            setUpdateLoading(true)
            saveProcessResult(payload)
                .then(res => {
                    const { success, message } = res
                    if (!success) {
                        $message.error(message)
                        reject(new Error(message))
                        return
                    }
                    setList(
                        tempList.map((_, index) => ({
                            show: _.system ? tempCheckedValue[_.text] : true,
                            text: _.text,
                            order: index,
                            system: _.system
                        }))
                    )
                    resolve()
                })
                .catch(reject)
                .finally(() => setUpdateLoading(true))
        })
    }

    const editProcessingResults = async () => {
        const flag = await getProcessingResults()
        if (!flag) {
            return
        }

        setTempList(cloneDeep(list))
        setTempCheckedValue(
            list.reduce((result, item) => {
                result[item.text] = item.system ? !!item.show : true
                return result
            }, {})
        )
        setUpdateLoading(false)
        createAPI(AddProcessingResultsModal)
    }

    const addProcessingResult = (label: string) => {
        if (!label) {
            return false
        }
        if (!/^[\u4e00-\u9fa5a-zA-Z\d\(\)（）\-\.\/\,\，\、]+$/.test(label)) {
            $message.error(`只能包含中文，英文，数字，横杠（-），点（.），正斜杠（/），顿号（、），小括号`)
            return false
        }
        if (tempList.length >= MAX_LIST_LENGTH) {
            $message.error(`最多自定义${MAX_LIST_LENGTH}个处理结果`)
            return false
        }
        if (tempList.find(_ => _.text === label)) {
            $message.error('处理结果不能重复')
            return false
        }
        setTempList(_ => [
            ..._,
            {
                text: label,
                system: false
            }
        ])
        return true
    }

    const sortProcessingResult = (oldIndex: number, newIndex: number) => {
        setTempList(_ => arrayMove(_, oldIndex, newIndex))
    }

    const checkProcessingResult = (text: string, checked: boolean) => {
        if (!checked && tempList.every(_ => _.system) && Object.values(tempCheckedValue).filter(_ => !!_).length <= 1) {
            $message.error('至少保留一个处理结果')
            return
        }
        setTempCheckedValue(_ => {
            return { ..._, [text]: checked }
        })
    }

    const deleteProcessingResult = (text: string) => {
        if (
            tempList.filter(_ => _.system).every(_ => !tempCheckedValue[_.text]) &&
            tempList.filter(_ => !_.system).length <= 1
        ) {
            $message.error('至少保留一个处理结果')
            return
        }
        setTempList(_list => _list.filter(_ => _.text !== text))
        setTempCheckedValue(_ => {
            return { ..._, [text]: false }
        })
    }

    const options = useMemo(() => {
        let arr = (list || [])
            .filter(_ => (_.system ? !!_.show : true))
            .map(_ => ({
                label: _.text,
                value: _.text,
                key: _.text,
                title: _.text
            }))
        arr.unshift({
            label: clearLabel,
            value: clearLabel,
            key: clearLabel,
            title: clearLabel
        })

        return arr
    }, [list])

    return {
        list,
        options,
        tempList,
        tempCheckedValue,
        loading,
        updateLoading,
        getProcessingResults,
        updateList,
        editProcessingResults,
        addProcessingResult,
        sortProcessingResult,
        checkProcessingResult,
        deleteProcessingResult
    }
}

export default useAfterSalesProcessingResults
